import { useEffect, useMemo, useRef, useState } from 'react'
import { Tree, type NodeRendererProps } from 'react-arborist'
import { BookOpen, CircleAlert, CircleDot, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { friendlyMessage } from '@/api/http'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { QualityScoreBadge } from '@/components/quality/QualityScoreBadge'
import { statusLabel } from '@/lib/statusLabel'
import type { QualityScore } from '@/components/quality/qualityGate'
import type { Chapter } from '@/types/nf'
import { formatNumber } from '@/utils/format'
import { getChapterDisplayTitle, sortChaptersByNumber } from '@/utils/chapterSelection'

interface ChapterTreeProps {
  chapters: Chapter[]
  selectedChapterId: string | null
  isLoading: boolean
  error: unknown
  isGenerating: boolean
  qualityByChapterId?: Record<string, QualityScore | null>
  onSelectChapter: (chapterId: string) => void
  onCreateChapter: () => void
}

interface ChapterNodeData {
  id: string
  chapter: Chapter
  quality?: QualityScore | null
}

function useElementHeight<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const updateHeight = () => setHeight(element.getBoundingClientRect().height)
    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return { ref, height }
}

export function getChapterStatusBadgeTitle(status: string | null | undefined, t: TFunction) {
  const normalized = status?.trim().toLowerCase()
  if (normalized === 'needs_review') return t('chapter.review.badgeTitle')
  if (normalized === 'partial_result') return t('chapter.partialResult.badgeTitle')
  return undefined
}

function ChapterNode({ node, style }: NodeRendererProps<ChapterNodeData>) {
  const { t } = useTranslation()
  const chapter = node.data.chapter
  const quality = node.data.quality
  const title = getChapterDisplayTitle(chapter, t('chapter.untitled'))
  const status = chapter.status || 'unknown'
  const visual = statusLabel(status, 'chapter')
  const words = chapter.word_count ?? 0

  return (
    // react-arborist 会把 width="100%" 解析成一次性测得的固定像素写进 style,
    // 容器变窄后行宽不跟随 -> 撑出横向滚动条且 truncate 失效。
    // 覆盖回 100% 让行始终贴合容器; top/height/position 仍用它给的值。
    <div style={{ ...style, width: '100%' }} className="min-w-0 px-3">
      <button
        type="button"
        className="flex h-full w-full min-w-0 items-center gap-2 rounded-md border border-transparent px-2.5 py-2 text-left text-sm outline-none transition-colors hover:border-border hover:bg-accent/45 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/35 data-[selected=true]:border-primary/50 data-[selected=true]:bg-primary/10"
        data-selected={node.isSelected}
        onClick={() => node.activate()}
      >
        <CircleDot className="size-3 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className="nf-mono shrink-0 text-xs text-muted-foreground">
              {t('chapter.numberShort', { value: chapter.chapter_number })}
            </span>
            <span className="truncate font-medium text-foreground">{title}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <Badge
              variant="outline"
              className="h-5 rounded-md px-1.5 text-xs font-normal"
              title={getChapterStatusBadgeTitle(status, t)}
            >
              {t(visual.textKey, visual.textOptions)}
            </Badge>
            <QualityScoreBadge quality={quality} compact />
            <span>{t('chapter.wordsValue', { value: formatNumber(words) })}</span>
          </div>
        </div>
      </button>
    </div>
  )
}

function ChapterTreeSkeleton() {
  return (
    <div className="space-y-2 p-3">
      {Array.from({ length: 10 }).map((_, index) => (
        <div key={index} className="rounded-md border bg-background/40 p-3">
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="mt-2 h-3 w-2/5" />
        </div>
      ))}
    </div>
  )
}

export function ChapterTree({
  chapters,
  selectedChapterId,
  isLoading,
  error,
  isGenerating,
  qualityByChapterId,
  onSelectChapter,
  onCreateChapter,
}: ChapterTreeProps) {
  const { t } = useTranslation()
  const { ref, height } = useElementHeight<HTMLDivElement>()
  const data = useMemo<ChapterNodeData[]>(
    () => sortChaptersByNumber(chapters).map((chapter) => ({ id: chapter.id, chapter, quality: qualityByChapterId?.[chapter.id] })),
    [chapters, qualityByChapterId],
  )

  return (
    <Card className="flex h-full min-h-[32rem] flex-col rounded-lg py-0">
      <CardHeader className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="size-4 text-primary" />
              {t('detail.chapterTree')}
            </CardTitle>
            <CardDescription>{t('detail.chapterTreeHint')}</CardDescription>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isGenerating}
            onClick={onCreateChapter}
            title={isGenerating ? t('chapter.generate.generatingDisabled') : t('chapter.generate.newChapter')}
          >
            <Plus className="size-4" />
            {t('chapter.generate.newChapter')}
          </Button>
        </div>
      </CardHeader>
      <Separator />
      {/* react-arborist(react-window) 的滚动容器按行内容测宽, 长章节标题会把它撑出
          一条无意义的横向滚动条。这里只保留纵向滚动, 让标题按 truncate 截断。
          层级: 本 div > arborist wrapper > react-window outer(overflow-x:auto)。 */}
      <div ref={ref} className="min-h-0 flex-1 [&>div>div]:overflow-x-hidden!">
        {isLoading ? <ChapterTreeSkeleton /> : null}
        {!isLoading && error ? (
          <div className="m-4 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            <div className="flex items-center gap-2 font-medium">
              <CircleAlert className="size-4" />
              {t('chapter.loadFailed')}
            </div>
            <p className="mt-2 text-destructive/80">
              {friendlyMessage(error, t('common.serviceError'))}
            </p>
          </div>
        ) : null}
        {!isLoading && !error && data.length === 0 ? (
          <div className="m-4 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            {t('detail.chapterEmpty')}
          </div>
        ) : null}
        {!isLoading && !error && data.length > 0 && height > 0 ? (
          <Tree<ChapterNodeData>
            aria-label={t('detail.chapterTree')}
            data={data}
            idAccessor="id"
            disableDrag
            disableEdit
            disableMultiSelection
            height={height}
            width="100%"
            rowHeight={64}
            overscanCount={8}
            selection={selectedChapterId ?? undefined}
            onActivate={(node) => onSelectChapter(node.data.chapter.id)}
          >
            {ChapterNode}
          </Tree>
        ) : null}
      </div>
    </Card>
  )
}
